import React, { useEffect, useRef } from 'react';

export const HeroInteractiveVisual = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    
    // Set canvas size
    const setSize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };
    setSize();

    // Particles representing thoughts/decisions
    const particles = [];
    const particleCount = 60;
    const connectionDistance = 120;
    const mouseInfluence = 150;

    // Colors from the theme
    const colors = {
      gold: { r: 201, g: 168, b: 124 },
      blue: { r: 90, g: 127, b: 168 },
      cyan: { r: 74, g: 158, b: 173 },
    };

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
        
        const colorChoice = Math.random();
        if (colorChoice < 0.4) {
          this.color = colors.gold;
        } else if (colorChoice < 0.7) {
          this.color = colors.blue;
        } else {
          this.color = colors.cyan;
        }
        this.alpha = Math.random() * 0.5 + 0.2;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulseOffset = Math.random() * Math.PI * 2;
      }

      update(time) {
        // Mouse influence
        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < mouseInfluence && dist > 0) {
          const force = (mouseInfluence - dist) / mouseInfluence * 0.02;
          this.vx += (dx / dist) * force;
          this.vy += (dy / dist) * force;
        }

        // Apply velocity with damping
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.99;
        this.vy *= 0.99;

        // Pulse animation
        this.currentAlpha = this.alpha + Math.sin(time * this.pulseSpeed + this.pulseOffset) * 0.15;

        // Bounce off edges
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
        
        // Keep in bounds
        this.x = Math.max(0, Math.min(width, this.x));
        this.y = Math.max(0, Math.min(height, this.y));
      }

      draw(ctx) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${this.currentAlpha})`;
        ctx.fill();
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Central core
    const core = {
      x: width * 0.65,
      y: height * 0.5,
      radius: 60,
      pulseRadius: 0,
    };

    // Orbiting rings
    const rings = [
      { radius: 100, speed: 0.0005, particles: 8, color: colors.blue },
      { radius: 150, speed: -0.0003, particles: 12, color: colors.gold },
      { radius: 200, speed: 0.0002, particles: 16, color: colors.cyan },
    ];

    let time = 0;

    const animate = () => {
      time++;
      ctx.clearRect(0, 0, width, height);

      // Update core position based on mouse
      const targetX = width * 0.65 + (mouseRef.current.x - width / 2) * 0.05;
      const targetY = height * 0.5 + (mouseRef.current.y - height / 2) * 0.05;
      core.x += (targetX - core.x) * 0.02;
      core.y += (targetY - core.y) * 0.02;

      // Draw connections between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < connectionDistance) {
            const alpha = (1 - dist / connectionDistance) * 0.15;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(90, 127, 168, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw orbiting rings
      rings.forEach((ring, ringIndex) => {
        const angle = time * ring.speed;
        
        // Draw ring path
        ctx.beginPath();
        ctx.arc(core.x, core.y, ring.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${ring.color.r}, ${ring.color.g}, ${ring.color.b}, 0.1)`;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 8]);
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw particles on ring
        for (let i = 0; i < ring.particles; i++) {
          const particleAngle = angle + (i / ring.particles) * Math.PI * 2;
          const px = core.x + Math.cos(particleAngle) * ring.radius;
          const py = core.y + Math.sin(particleAngle) * ring.radius;
          const size = 2 + Math.sin(time * 0.02 + i) * 1;
          
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${ring.color.r}, ${ring.color.g}, ${ring.color.b}, ${0.4 + Math.sin(time * 0.02 + i) * 0.2})`;
          ctx.fill();
        }
      });

      // Draw core glow
      const gradient = ctx.createRadialGradient(core.x, core.y, 0, core.x, core.y, core.radius * 2);
      gradient.addColorStop(0, 'rgba(201, 168, 124, 0.3)');
      gradient.addColorStop(0.5, 'rgba(90, 127, 168, 0.1)');
      gradient.addColorStop(1, 'rgba(90, 127, 168, 0)');
      ctx.beginPath();
      ctx.arc(core.x, core.y, core.radius * 2, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw pulsing core
      core.pulseRadius = 15 + Math.sin(time * 0.03) * 5;
      ctx.beginPath();
      ctx.arc(core.x, core.y, core.pulseRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(201, 168, 124, 0.8)';
      ctx.fill();

      // Draw core inner ring
      ctx.beginPath();
      ctx.arc(core.x, core.y, 35, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(201, 168, 124, 0.4)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Update and draw particles
      particles.forEach(particle => {
        particle.update(time);
        particle.draw(ctx);

        // Connect particles to core if close enough
        const dx = particle.x - core.x;
        const dy = particle.y - core.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 250 && dist > 50) {
          const alpha = (1 - dist / 250) * 0.2;
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(core.x, core.y);
          ctx.strokeStyle = `rgba(201, 168, 124, ${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Mouse move handler
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    // Resize handler
    const handleResize = () => {
      setSize();
      core.x = width * 0.65;
      core.y = height * 0.5;
      particles.forEach(p => {
        if (p.x > width) p.x = width;
        if (p.y > height) p.y = height;
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      canvas.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ opacity: 0.9 }}
      />
      {/* Gradient overlays to blend edges */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#fffef2] via-transparent to-transparent w-1/3" />
      <div className="absolute inset-0 bg-gradient-to-l from-[#fffef2]/50 via-transparent to-transparent w-1/4 right-0 left-auto" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#fffef2] via-transparent to-transparent h-1/4" />
    </div>
  );
};

export default HeroInteractiveVisual;
